import { Controller } from "@hotwired/stimulus"

// PCO "Acknowledge & send" modal (builder module). Clicking the header button
// opens this: the GC is recorded as acknowledging the proposed change order,
// and optionally sends it to the owner (pre-filled, editable) and anyone else
// to acknowledge too. Acknowledgment is optional and non-binding.
export default class extends Controller {
  static targets = ["modal", "rows", "error", "submit", "team", "teamWrap"]
  static values = { url: String, defaultRecipient: Object, savedRecipients: Array, team: Array }

  open() {
    if (this.rowsTarget.children.length === 0) {
      // Defensive reads — a malformed value must never kill the click.
      // Priority: the last list sent on this project > the owner default.
      let saved = []
      try { saved = this.savedRecipientsValue || [] } catch (e) { saved = [] }
      let d = null
      try { d = this.defaultRecipientValue } catch (e) { d = null }
      if (saved.length) {
        saved.forEach((r) => this._addRow(r.name, r.email))
      } else if (d && d.email) {
        this._addRow(d.name, d.email)
      } else {
        this._addRow("", "")
      }
    }
    this._renderTeam()
    this.errorTarget.classList.add("hidden")
    this.modalTarget.classList.remove("hidden")
  }

  // One-click recipients from the project team (Larry 2026-07-31): each
  // member renders as a chip; clicking fills a row with their name + email.
  _renderTeam() {
    if (!this.hasTeamTarget) return
    let team = []
    try { team = this.teamValue || [] } catch (e) { team = [] }
    if (!team.length) return

    this.teamTarget.innerHTML = ""
    team.forEach((m) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-gray-200 bg-gray-50 hover:bg-blue-50 hover:border-blue-200 text-[12px] text-gray-700 cursor-pointer"
      btn.title = m.label ? `${m.email} — ${m.label}` : m.email
      btn.textContent = `+ ${m.name}`
      btn.addEventListener("click", () => this._addTeamMember(m))
      this.teamTarget.appendChild(btn)
    })
    this.teamWrapTarget.classList.remove("hidden")
  }

  _addTeamMember(m) {
    const existing = Array.from(this.rowsTarget.querySelectorAll("[data-ack-email]"))
    const already = existing.some((i) => i.value.trim().toLowerCase() === m.email.toLowerCase())
    if (already) return

    // Reuse a fully empty row when one is sitting there; otherwise append.
    const emptyRow = existing.find((i) => i.value.trim() === "")
    if (emptyRow) {
      emptyRow.value = m.email
      const nameInput = emptyRow.closest("[data-ack-row]").querySelector("[data-ack-name]")
      if (nameInput && nameInput.value.trim() === "") nameInput.value = m.name
    } else {
      this._addRow(m.name, m.email)
    }
  }

  close() { this.modalTarget.classList.add("hidden") }
  stop(e) { e.stopPropagation() }

  addRow() { this._addRow("", "") }

  removeRow(e) { e.currentTarget.closest("[data-ack-row]").remove() }

  _addRow(name, email) {
    const esc = (s) => (s || "").replace(/"/g, "&quot;")
    const div = document.createElement("div")
    div.dataset.ackRow = ""
    div.className = "flex flex-wrap sm:flex-nowrap items-center gap-2"
    div.innerHTML = `
      <input type="email" data-ack-email placeholder="email@company.com" value="${esc(email)}"
             class="w-full sm:w-2/5 min-w-0 border border-gray-300 rounded-lg px-2.5 py-1.5 text-[13px]">
      <input type="text" data-ack-name placeholder="Name (optional)" value="${esc(name)}"
             class="flex-1 border border-gray-300 rounded-lg px-2.5 py-1.5 text-[13px]">
      <button type="button" data-action="click->pco-ack#removeRow" class="text-gray-400 hover:text-red-500 cursor-pointer" title="Remove">✕</button>`
    this.rowsTarget.appendChild(div)
  }

  async submit() {
    const recipients = []
    let bad = false
    this.rowsTarget.querySelectorAll("[data-ack-row]").forEach((row) => {
      const email = row.querySelector("[data-ack-email]").value.trim()
      const name = row.querySelector("[data-ack-name]").value.trim()
      if (!email) return
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) bad = true
      recipients.push({ name, email, role: "owner" })
    })
    if (bad) return this._err("One of the emails looks invalid — please check it.")

    this.submitTarget.disabled = true
    try {
      const resp = await fetch(this.urlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json", "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
        },
        body: JSON.stringify({ recipients })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) { this.submitTarget.disabled = false; return this._err(data.error || "Could not send.") }

      const sent = data.sent || 0
      sessionStorage.setItem("builder-finance.flash",
        sent > 0 ? `Acknowledged — sent to ${sent} ${sent === 1 ? "person" : "people"} to acknowledge.` : "Acknowledged.")
      window.location.reload()
    } catch {
      this.submitTarget.disabled = false
      this._err("Network error — please try again.")
    }
  }

  _err(msg) {
    this.errorTarget.textContent = msg
    this.errorTarget.classList.remove("hidden")
  }
}
